import {
  ChartFrequency,
  IComposableChartsWidget,
  IPostgreJobsAnalyticsPayload,
} from 'wisestep-api';

import { CvViewBarChartPostgre, CvViewTablePostgre } from '../../../BaseCharts';
import { CvViewWidget } from '../../../BaseCharts/jobs/types/postgreJobsWidgetProp';
import { ChartScope } from '../../../types';
import {
  CvViewTableJobBoardWidget,
  CvViewExtraWidget,
  getChartFreqForTimePeriod,
  CvViewBarChartJobBoardWidget,
} from '../../helper';
import { jobsTimePeriodList } from '../../Jobs/helper';

import { jobBoards } from './cvViewHelper';

const getCvViewChartWidgetByTimeAndScope = (
  timePeriodId: string,
  scope: ChartScope,
  frequency: ChartFrequency = getChartFreqForTimePeriod[timePeriodId]
): Partial<Record<CvViewWidget, IComposableChartsWidget>> => {
  const defaultQuery: IPostgreJobsAnalyticsPayload = {
    category: 'cvview',
    filters: [],
    type: 'total-metric',
    range: timePeriodId,
    scope,
    frequency,
  };
  const freqInRegularCase = frequency.charAt(0).toUpperCase() + frequency.slice(1).toLowerCase();

  const widgetConfig = {
    [CvViewExtraWidget.TABLE_SOURCE]: {
      name: 'Download Source Data',
      filters: {},
      category: defaultQuery.category as string,
      type: `table_cvview_source_${timePeriodId}_${scope}_postgre`,
      component: (
        <CvViewTablePostgre
          query={{ ...defaultQuery, type: 'download-source-table' }}
          title="Download Source Data"
          widgetName={CvViewExtraWidget.TABLE_SOURCE}
          widgetType={`table_cvview_source_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [CvViewExtraWidget.TABLE_JOB_BOARD]: {
      name: 'CVs Viewed by Job Boards',
      filters: {},
      category: defaultQuery.category as string,
      type: `table_cvview_jobboard_${timePeriodId}_${scope}_postgre`,
      component: (
        <CvViewTablePostgre
          query={{ ...defaultQuery, type: 'jobboard-table' }}
          title="CVs Viewed by Job Boards"
          widgetName={CvViewExtraWidget.TABLE_JOB_BOARD}
          widgetType={`table_cvview_jobboard_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    [CvViewExtraWidget.TABLE_RECRUITER]: {
      name: 'Total CVs Viewed by Recruiters',
      filters: {},
      category: defaultQuery.category as string,
      type: `table_cvview_recruiter_${timePeriodId}_${scope}_postgre`,
      component: (
        <CvViewTablePostgre
          query={{ ...defaultQuery, type: 'recruiter-table' }}
          title="Total CVs Viewed by Recruiters"
          widgetName={CvViewExtraWidget.TABLE_RECRUITER}
          widgetType={`table_cvview_recruiter_${timePeriodId}_${scope}_postgre`}
        />
      ),
    },
    ...Object.fromEntries(
      Object.values(CvViewTableJobBoardWidget).map((widgetName, index) => [
        widgetName,
        {
          name: `CVs Viewed by Users - ${jobBoards[index]}`,
          filters: {},
          category: defaultQuery.category as string,
          type: `table_cvview_jobboard_recruiter_${jobBoards[index]}_${timePeriodId}_${scope}_postgre`,
          component: (
            <CvViewTablePostgre
              query={{
                ...defaultQuery,
                type: `${jobBoards[index]
                  .replaceAll(' ', '')
                  .toLowerCase()}-jobboard-recruiter-table`,
              }}
              title={`CVs Viewed by Users - ${jobBoards[index]}`}
              widgetName={widgetName}
              widgetType={`table_cvview_jobboard_recruiter_${jobBoards[index]}_${timePeriodId}_${scope}_postgre`}
            />
          ),
        },
      ])
    ),
    ...Object.fromEntries(
      Object.values(CvViewBarChartJobBoardWidget).map((widgetName, index) => [
        widgetName,
        {
          name: `CVs Viewed by ${jobBoards[index]} - ${freqInRegularCase}`,
          filters: {},
          category: defaultQuery.category as string,
          type: `barchart_cvview_jobboard_${jobBoards[index]}_${timePeriodId}_${scope}_postgre`,
          component: (
            <CvViewBarChartPostgre
              query={{
                ...defaultQuery,
                type: `${jobBoards[index].replaceAll(' ', '').toLowerCase()}-jobboard-barchart`,
              }}
              title={`CVs Viewed by ${jobBoards[index]} ${freqInRegularCase}`}
              widgetName={widgetName}
            />
          ),
        },
      ])
    ),
  };

  return widgetConfig;
};

export const cvViewChartsKeyPostgre = (
  timePeriodId: string,
  scope: ChartScope
): IComposableChartsWidget[] => {
  const defaultWidgetList = Object.values(getCvViewChartWidgetByTimeAndScope(timePeriodId, scope));
  const extraWidgetList = {};

  return [...defaultWidgetList, ...(extraWidgetList[timePeriodId] ?? [])];
};

export const postgreCvViewDashboardKeysList = jobsTimePeriodList.flatMap((timePeriod) => [
  ...cvViewChartsKeyPostgre(timePeriod.id, ChartScope.WORKSPACE),
  ...cvViewChartsKeyPostgre(timePeriod.id, ChartScope.LOGGED_IN_USER),
]);
